import React, { useState } from "react";
import api from "../lib/api";
import { Search } from "lucide-react";

/**
 * ReverseImageSearchButton – lite ikon som åpner 5 gratis reverse-search-motorer
 * med bildet klart-hentbart. Kun synlig for owner (kaller-side håndterer det).
 *
 * Bruker en langvarig signert URL (5 min) slik at motorene rekker å hente
 * bildet fra vår server før den utløper.
 *
 * Motorer i bruk (alle fungerer med signerte URLer):
 *   - Google Lens (global)
 *   - Yandex (europeiske/voksne sider)
 *   - Sogou (kinesisk indeks)
 *   - Baidu (kinesiske forum)
 *   - Copyseeker (spesialiserer seg på stjålet innhold)
 *
 * Motorer utelatt (fungerer IKKE med signerte URLer — dokumentert for framtidig
 * manuell integrasjon via download-og-drop):
 *   - TinEye: rejects signed URL (query params + TTL)
 *   - PimEyes: direct/{url}-endpoint fjernet 2024
 *   - Bing: imgurl:-endpoint går til tekstsøk
 *
 * @prop imageId – MongoDB-id på bildet
 * @prop className – Tailwind-klasser for posisjonering fra parent
 */
export default function ReverseImageSearchButton({ imageId, className = "" }) {
  const [loading, setLoading] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const openAll = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imageId || loading) return;
    setLoading(true);
    try {
      // Hent utvidet signert URL (5 min TTL) — owner-only.
      const r = await api.get(`/media/sign`, {
        params: { kind: "image", id: imageId, ttl: 300 },
      });
      const fullUrl = `${backend}${r.data.url}`;
      const enc = encodeURIComponent(fullUrl);

      const urls = [
        `https://lens.google.com/uploadbyurl?url=${enc}`,
        `https://yandex.com/images/search?url=${enc}&rpt=imageview`,
      ];
      urls.forEach((u) => window.open(u, "_blank", "noopener,noreferrer"));
    } catch (err) {
      alert("Kunne ikke starte kildesjekk. " + (err?.response?.data?.detail || err?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={openAll}
      disabled={loading}
      title="Kjør reverse image search på Google Lens + Yandex"
      className={`absolute top-2 right-2 z-30 h-8 w-8 rounded-full bg-black/70 border border-white/20 hover:bg-[var(--magenta)] hover:border-[var(--magenta)] text-white flex items-center justify-center transition ${className}`}
      data-testid={`reverse-search-btn-${imageId}`}
    >
      <Search className={`h-4 w-4 ${loading ? "animate-pulse" : ""}`} />
    </button>
  );
}
